import { useEffect, useRef, useState } from "react"
import { Check, Copy, ThumbsDown, ThumbsUp, X } from "@phosphor-icons/react"
import { trackEvent } from "~/hooks/useTrackingService"
import { useHakiStream } from "~/hooks/useHakiStream"
import { usePageContext } from "~/hooks/usePageContext"
import { useHakiBottomBarStore } from "~/stores/haki-bottom-bar-store"
import { api } from "~/utils/api"
import { Button, IconButton } from "~/components/reusable/Button"
import ChatMarkdown from "~/components/reusable/ChatMarkdown"
import HakiFace from "~/components/reusable/HakiFace"
import HakiGlow from "~/components/reusable/HakiGlow"
import {
  Popover,
  PopoverContent,
  PopoverSection,
  PopoverTrigger,
} from "~/components/reusable/Popover"
import { Tooltip } from "~/components/reusable/Tooltip"
import { getToolStatusDisplay } from "~/components/sidebar/chat/haki-tool-status"
import type { PageContext } from "~/server/api/routers/haki/types"

interface HakiPromptPopoverProps {
  /** Trigger button label. */
  triggerText: string
  /** Pre-canned user message sent to Haki on first open. Persisted to the thread. */
  prompt: string
  /** Disable the trigger. */
  disabled?: boolean
  /** Tooltip text when disabled. */
  disabledReason?: string
  /** Override the page context sent with the chat. Defaults to usePageContext(). */
  pageContext?: PageContext
  /** Tracking event prefix, e.g. "Forecast Explainer" -> "Forecast Explainer Opened". */
  trackingName?: string
  /** Source tag for Langfuse tracing and token usage attribution. Defaults to "chat". */
  source?: "chat" | "explain-forecast"
}

export default function HakiPromptPopover({
  triggerText,
  prompt,
  disabled = false,
  disabledReason,
  pageContext: pageContextOverride,
  trackingName,
  source,
}: HakiPromptPopoverProps) {
  const [open, setOpen] = useState(false)
  const [threadId, setThreadId] = useState<number | null>(null)
  const [rating, setRating] = useState<"up" | "down" | null>(null)
  const [copied, setCopied] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const startedRef = useRef(false)

  const derivedPageContext = usePageContext()
  const pageContext = pageContextOverride ?? derivedPageContext

  const openBottomBarChat = useHakiBottomBarStore((s) => s.openChat)
  const sendMessageMutation = api.haki.sendMessage.useMutation()
  const utils = api.useUtils()

  const stream = useHakiStream({
    onComplete: () => {
      utils.haki.listThreads.invalidate()
      if (threadId != null) utils.haki.getThread.invalidate({ threadId })
    },
  })
  const { isStreaming, streamingText, toolStatus, stale, finalContent, error, committedBlocks } =
    stream

  useEffect(() => {
    if (!open || startedRef.current) return
    startedRef.current = true
    if (trackingName) trackEvent(`${trackingName} Opened`)
    ;(async () => {
      try {
        const { threadId: newThreadId } = await sendMessageMutation.mutateAsync({
          content: prompt,
        })
        setThreadId(newThreadId)
        stream.send(newThreadId, pageContext, source)
        utils.haki.listThreads.invalidate()
      } catch (err) {
        console.error("[HakiPromptPopover] failed to start thread:", err)
        setStartError(
          err instanceof Error ? err.message : "Something went wrong. Please try again.",
        )
      }
    })()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => setOpen(false)

  const handleContinueInHaki = () => {
    if (threadId == null) return
    if (trackingName) trackEvent(`${trackingName} Continued In Haki`)
    openBottomBarChat(threadId)
    setOpen(false)
  }

  const handleRate = (kind: "up" | "down") => {
    const next = rating === kind ? null : kind
    setRating(next)
    if (next && trackingName)
      trackEvent(`${trackingName} Feedback`, {
        Feedback: next === "up" ? "Helpful" : "Not helpful",
      })
  }

  const handleCopy = async () => {
    if (!finalContent) return
    try {
      await navigator.clipboard.writeText(finalContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  const showLoader = !threadId || (isStreaming && !streamingText)
  const toolStatusLabel = isStreaming ? getToolStatusDisplay(toolStatus, stale).label : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip title={disabled ? (disabledReason ?? "") : ""}>
        <PopoverTrigger asChild>
          <div
            className="rounded-full p-px inline-flex"
            style={{
              backgroundImage: "var(--haki-brand-gradient)",
            }}
          >
            <Button size="sm" variant="neutral" disabled={disabled} className="shadow-none bg-base">
              <HakiFace size={16} />
              {triggerText}
            </Button>
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        sideOffset={8}
        side="bottom"
        align="end"
        collisionPadding={16}
        className="w-[460px] max-w-[var(--radix-popover-content-available-width)] max-h-[var(--radix-popover-content-available-height)] paragraph-base p-0 bg-transparent shadow-none border-0"
      >
        <HakiGlow>
          <div className="bg-base rounded-[16px] shadow-flyout flex flex-col divide-y divide-border-base overflow-hidden max-h-[var(--radix-popover-content-available-height)]">
            <PopoverSection className="rounded-none p-3 flex flex-row items-center gap-2 shrink-0">
              <HakiFace size={16} working={showLoader} />
              <div className={`label-base flex-1 ${showLoader ? "animate-pulse" : ""}`}>
                {triggerText}
              </div>
              <IconButton size="sm" variant="transparent" onClick={handleClose}>
                <X />
              </IconButton>
            </PopoverSection>

            <PopoverSection className="rounded-none p-4 min-h-0 flex-1 overflow-hidden flex flex-col">
              <div className="overflow-y-auto min-h-0 flex-1 pr-1 -mr-1">
                {(error ?? startError) ? (
                  <div className="paragraph-base text-fg-danger">{error ?? startError}</div>
                ) : finalContent ? (
                  <div className="flex flex-col gap-2">
                    <ChatMarkdown
                      content={finalContent}
                      blocks={committedBlocks}
                      threadId={threadId ?? undefined}
                      compact
                    />
                    <div className="flex flex-row items-center gap-1">
                      <Tooltip title={copied ? "Copied" : "Copy answer"}>
                        <IconButton variant="transparent" size="sm" onClick={handleCopy}>
                          {copied ? <Check /> : <Copy />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Good answer">
                        <IconButton
                          variant="transparent"
                          size="sm"
                          onClick={() => handleRate("up")}
                        >
                          <ThumbsUp weight={rating === "up" ? "fill" : "bold"} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bad answer">
                        <IconButton
                          variant="transparent"
                          size="sm"
                          onClick={() => handleRate("down")}
                        >
                          <ThumbsDown weight={rating === "down" ? "fill" : "bold"} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ) : isStreaming ? (
                  streamingText ? (
                    <ChatMarkdown content={streamingText} compact streaming />
                  ) : (
                    <span className="text-shimmer text-fg-subtle">{toolStatusLabel}</span>
                  )
                ) : (
                  <div className="animate-pulse text-fg-subtle">...</div>
                )}
              </div>
            </PopoverSection>

            <PopoverSection className="p-4 flex flex-row items-center rounded-none shrink-0">
              <Button
                size="xs"
                variant="neutral"
                onClick={handleContinueInHaki}
                disabled={threadId == null}
              >
                Ask a follow-up
              </Button>
            </PopoverSection>
          </div>
        </HakiGlow>
      </PopoverContent>
    </Popover>
  )
}
